import React, { useState, useEffect } from 'react';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  Divider,
  Chip,
  Alert,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import { Video, Matiere, Classe } from '../../types';
import CurriculumApiService from '../../services/curriculumApi';
import { fetchYoutubeMetadata } from '../../services/youtubeMetadataApi';
import { toUserMessage } from '../../lib/http';
import type { FieldChangeEvent } from '../../types/forms';
import { logger } from '../../lib/logger';

interface VideoFormProps {
  video?: Video | null;
  onSubmit: (data: Omit<Video, 'id'>) => void;
  onCancel: () => void;
}

const VideoForm: React.FC<VideoFormProps> = ({ video, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<Omit<Video, 'id'>>({
    title: '',
    description: '',
    subject: '',
    duration: 0,
    youtubeUrl: '',
    thumbnailUrl: '',
    niveauScolaireId: '',
    tags: [],
    views: 0,
    completionCount: 0,
    averageWatchPercentage: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    lastUpdated: new Date().toISOString()
  });

  const [newTag, setNewTag] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [matieres, setMatieres] = useState<Matiere[]>([]);
  const [classes, setClasses] = useState<Classe[]>([]);
  const [loadingMatieres, setLoadingMatieres] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingVideoInfo, setLoadingVideoInfo] = useState(false);
  const { environment: env } = useEnvironment();

  // Charger les matières depuis la base de données
  useEffect(() => {
    const loadMatieres = async () => {
      try {
        setLoadingMatieres(true);
        const data = await CurriculumApiService.getMatieres(env);
        setMatieres(data.filter(m => m.estActif)); // Uniquement les matières actives
      } catch (error) {
        logger.error('Erreur lors du chargement des matières:', error);
        setErrors(prev => [...prev, 'Impossible de charger les matières']);
      } finally {
        setLoadingMatieres(false);
      }
    };

    loadMatieres();
  }, [env]);

  // Charger les classes depuis la base de données
  useEffect(() => {
    const loadClasses = async () => {
      try {
        setLoadingClasses(true);
        const data = await CurriculumApiService.getClasses(env);
        setClasses(data.filter(c => c.estActif)); // Uniquement les classes actives
      } catch (error) {
        logger.error('Erreur lors du chargement des classes:', error);
        setErrors(prev => [...prev, 'Impossible de charger les classes']);
      } finally {
        setLoadingClasses(false);
      }
    };

    loadClasses();
  }, [env]);

  useEffect(() => {
    if (video) {
      setFormData({
        title: video.title || '',
        description: video.description || '',
        subject: video.subject || '',
        duration: video.duration || 0,
        youtubeUrl: video.youtubeUrl || '',
        thumbnailUrl: video.thumbnailUrl || '',
        niveauScolaireId: video.niveauScolaireId || '',
        tags: video.tags || [],
        views: video.views || 0,
        completionCount: video.completionCount || 0,
        averageWatchPercentage: video.averageWatchPercentage || 0,
        isActive: video.isActive ?? true,
        createdAt: video.createdAt || new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      });
    }
  }, [video]);

  const handleChange = (field: keyof Omit<Video, 'id'>) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    let value: string | number | boolean;

    if (event.target.type === 'checkbox') {
      value = (event.target as HTMLInputElement).checked;
    } else if (event.target.type === 'number') {
      value = parseFloat(event.target.value) || 0;
    } else {
      value = event.target.value;
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSelectChange = (field: keyof Omit<Video, 'id'>) => (
    event: FieldChangeEvent
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleDeleteTag = (tagToDelete: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToDelete)
    }));
  };

  // Extraire l'ID YouTube de l'URL
  const extractYouTubeId = (url: string): string => {
    const regex = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/;
    const match = url.match(regex);
    return match ? match[1] : '';
  };

  // Récupérer la durée et les vues depuis YouTube (via le backend : la clé
  // YouTube reste côté serveur)
  const fetchYouTubeVideoInfo = async (videoId: string) => {
    try {
      setLoadingVideoInfo(true);
      const metadata = await fetchYoutubeMetadata(videoId);
      setFormData(prev => ({
        ...prev,
        duration: metadata.duration,
        views: metadata.views
      }));
    } catch (error) {
      const errorMessage = toUserMessage(error, 'Impossible de récupérer les données de la vidéo YouTube');
      setErrors(prev => [...prev, errorMessage]);
    } finally {
      setLoadingVideoInfo(false);
    }
  };

  // Auto-générer la miniature et récupérer la durée depuis l'URL YouTube
  const handleYouTubeUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const url = event.target.value;
    setFormData(prev => ({
      ...prev,
      youtubeUrl: url
    }));

    // Auto-générer l'URL de la miniature et récupérer la durée
    const videoId = extractYouTubeId(url);
    if (videoId) {
      setFormData(prev => ({
        ...prev,
        thumbnailUrl: `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`
      }));

      // Récupérer la durée depuis YouTube API
      fetchYouTubeVideoInfo(videoId);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!formData.title || formData.title.trim().length < 3) {
      newErrors.push('Le titre doit contenir au moins 3 caractères');
    }

    if (!formData.description || formData.description.trim().length < 10) {
      newErrors.push('La description doit contenir au moins 10 caractères');
    }

    if (!formData.youtubeUrl) {
      newErrors.push('L\'URL YouTube est requise');
    } else if (!formData.youtubeUrl.match(/(?:youtube\.com|youtu\.be)/)) {
      newErrors.push('URL YouTube invalide');
    }

    if (formData.duration < 1) {
      newErrors.push('La durée doit être supérieure à 0');
    }

    if (formData.averageWatchPercentage < 0 || formData.averageWatchPercentage > 1) {
      newErrors.push('Le pourcentage de visionnage doit être entre 0 et 1');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Mettre à jour la date de dernière modification
    const dataToSubmit = {
      ...formData,
      lastUpdated: new Date().toISOString()
    };

    onSubmit(dataToSubmit);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Veuillez corriger les erreurs suivantes :
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Section: Informations principales */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom color="primary">
          Informations principales
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Titre de la vidéo"
              value={formData.title}
              onChange={handleChange('title')}
              required
              helperText="Minimum 3 caractères"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={handleChange('description')}
              multiline
              rows={4}
              required
              helperText="Minimum 10 caractères"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={loadingMatieres}>
              <InputLabel>Matière</InputLabel>
              <Select
                value={formData.subject}
                label="Matière"
                onChange={handleSelectChange('subject')}
                startAdornment={
                  loadingMatieres ? (
                    <CircularProgress size={20} sx={{ ml: 1 }} />
                  ) : null
                }
              >
                {matieres.length === 0 && !loadingMatieres ? (
                  <MenuItem disabled>Aucune matière disponible</MenuItem>
                ) : (
                  matieres.map(matiere => (
                    <MenuItem key={matiere.id} value={matiere.nom}>
                      {matiere.nom}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={loadingClasses}>
              <InputLabel>Niveau scolaire (Classe)</InputLabel>
              <Select
                value={formData.niveauScolaireId}
                label="Niveau scolaire (Classe)"
                onChange={handleSelectChange('niveauScolaireId')}
                startAdornment={
                  loadingClasses ? (
                    <CircularProgress size={20} sx={{ ml: 1 }} />
                  ) : null
                }
              >
                <MenuItem value="">
                  <em>Aucun</em>
                </MenuItem>
                {classes.length === 0 && !loadingClasses ? (
                  <MenuItem disabled>Aucune classe disponible</MenuItem>
                ) : (
                  classes.map(classe => (
                    <MenuItem key={classe.id} value={classe.id}>
                      {classe.nom}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Section: Contenu YouTube */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom color="primary">
          Contenu YouTube
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="URL YouTube"
              value={formData.youtubeUrl}
              onChange={handleYouTubeUrlChange}
              required
              helperText="Format: https://www.youtube.com/watch?v=... ou https://youtu.be/..."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Durée (secondes)"
              type="number"
              value={formData.duration}
              onChange={handleChange('duration')}
              disabled={loadingVideoInfo}
              required
              InputProps={{
                inputProps: { min: 1 },
                endAdornment: loadingVideoInfo ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : null
              }}
              helperText={
                loadingVideoInfo
                  ? 'Récupération de la durée depuis YouTube...'
                  : formData.duration > 0
                  ? `Format: ${Math.floor(formData.duration / 60)}:${(formData.duration % 60).toString().padStart(2, '0')} - Auto-généré depuis YouTube ou saisie manuelle`
                  : 'Saisir l\'URL YouTube pour récupération automatique, ou saisir manuellement'
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="URL de la miniature"
              value={formData.thumbnailUrl}
              onChange={handleChange('thumbnailUrl')}
              helperText="Auto-généré depuis l'URL YouTube"
            />
          </Grid>
          {formData.thumbnailUrl && (
            <Grid item xs={12}>
              <Box sx={{ maxWidth: 300, mx: 'auto' }}>
                <img
                  src={formData.thumbnailUrl}
                  alt="Aperçu miniature"
                  style={{ width: '100%', borderRadius: 8 }}
                  onError={(event) => {
                    (event.target as HTMLImageElement).src = 'https://via.placeholder.com/480x360?text=Miniature+non+disponible';
                  }}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Section: Organisation et tags */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom color="primary">
          Organisation et tags
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box display="flex" gap={1} mb={1}>
              <TextField
                fullWidth
                label="Ajouter un tag"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleAddTag();
                  }
                }}
                size="small"
              />
              <Button
                variant="outlined"
                onClick={handleAddTag}
                disabled={!newTag.trim()}
              >
                Ajouter
              </Button>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {formData.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  onDelete={() => handleDeleteTag(tag)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Section: Statistiques */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom color="primary">
          Statistiques
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Nombre de vues"
              type="number"
              value={formData.views}
              onChange={handleChange('views')}
              disabled={loadingVideoInfo}
              InputProps={{
                inputProps: { min: 0 },
                endAdornment: loadingVideoInfo ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : null
              }}
              helperText={
                loadingVideoInfo
                  ? 'Récupération depuis YouTube...'
                  : formData.views > 0
                  ? `Auto-récupéré depuis YouTube ou saisie manuelle`
                  : 'Saisir l\'URL YouTube pour récupération automatique'
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Nombre de complétions"
              type="number"
              value={formData.completionCount}
              onChange={handleChange('completionCount')}
              InputProps={{
                inputProps: { min: 0 }
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Pourcentage moyen de visionnage"
              type="number"
              value={formData.averageWatchPercentage}
              onChange={handleChange('averageWatchPercentage')}
              InputProps={{
                inputProps: { min: 0, max: 1, step: 0.01 },
                endAdornment: <InputAdornment position="end">
                  ({(formData.averageWatchPercentage * 100).toFixed(0)}%)
                </InputAdornment>
              }}
              helperText="Valeur entre 0 et 1"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Section: Paramètres */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom color="primary">
          Paramètres
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <FormControlLabel
          control={
            <Switch
              checked={formData.isActive}
              onChange={handleChange('isActive')}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1">
                Vidéo active
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formData.isActive
                  ? 'La vidéo est visible et accessible aux utilisateurs'
                  : 'La vidéo est masquée et non accessible'}
              </Typography>
            </Box>
          }
        />
      </Paper>

      {/* Boutons d'action */}
      <Box display="flex" gap={2} justifyContent="flex-end">
        <Button
          variant="outlined"
          onClick={onCancel}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
        >
          {video ? 'Mettre à jour' : 'Créer'}
        </Button>
      </Box>
    </Box>
  );
};

export default VideoForm;
